import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Auth Pages
import Login from './pages/auth/Login';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import UsersManagement from './pages/admin/UsersManagement';
import PropertiesManagement from './pages/admin/PropertiesManagement';
import ToursManagement from './pages/admin/ToursManagement';
import RentManagement from './pages/admin/RentManagement';
import ContactManagement from './pages/admin/ContactManagement';
import AgentManagement from './pages/admin/AgentManagement';
import PropertyManagementRequests from './pages/admin/PropertyManagementRequests';
import CouponManagement from './pages/admin/CouponManagement';

// Layout Components
import AdminLayout from './components/layout/AdminLayout';
import AgentLayout from './components/layout/AgentLayout';

// Auth Provider
import { AuthProvider, useAuth } from './context/AuthContext';

// Protected Route Component
const ProtectedRoute = ({ children, roles }: { children: React.ReactNode, roles: any[] }) => {
  const { admin, isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (roles && !roles.includes(admin?.role)) {
    return <Navigate to="/unauthorized" />;
  }
  
  return <>{children}</>;
};

// Admin Protected Route
const AdminRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute roles={['ADMIN']}>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

// Agent Protected Route
const AgentRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute roles={['ADMIN', 'AGENT']}>
    <AgentLayout>{children}</AgentLayout>
  </ProtectedRoute>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<div className="p-8 text-center">You are not authorized to access this page.</div>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><UsersManagement /></AdminRoute>} />
          <Route path="/admin/properties" element={<AdminRoute><PropertiesManagement /></AdminRoute>} />
          <Route path="/admin/tours" element={<AdminRoute><ToursManagement /></AdminRoute>} />
          <Route path="/admin/rents" element={<AdminRoute><RentManagement /></AdminRoute>} />
          <Route path="/admin/contacts" element={<AdminRoute><ContactManagement /></AdminRoute>} />
          <Route path="/admin/agents" element={<AdminRoute><AgentManagement /></AdminRoute>} />
          <Route path="/admin/coupons" element={<AdminRoute><CouponManagement /></AdminRoute>} />
          <Route path="/admin/property-management-requests" element={<AdminRoute><PropertyManagementRequests /></AdminRoute>} />
          
          {/* Agent Routes */}
          <Route path="/agent" element={<Navigate to="/agent/dashboard" replace />} />
          <Route path="/agent/dashboard" element={<AgentRoute><Dashboard /></AgentRoute>} />
          <Route path="/agent/properties" element={<AgentRoute><PropertiesManagement /></AgentRoute>} />
          <Route path="/agent/tours" element={<AgentRoute><ToursManagement /></AgentRoute>} />
          <Route path="/agent/rents" element={<AgentRoute><RentManagement /></AgentRoute>} />
          
          {/* Redirect root to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* Not Found Page */}
          <Route path="*" element={<div className="p-8 text-center">Page not found.</div>} />
        </Routes>
      </Router>
      <ToastContainer position="top-right" autoClose={3000} />
    </AuthProvider>
  );
};

export default App;
